import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Theme,
  Typography,
  useTheme
} from '@mui/material';
import { Agency, AgencyQuestion, AgencyQuestionKind } from '../../../utils/types';
import { useCallback, useEffect, useState } from 'react';
import { useUser } from '../../../hooks/useUser';
import { COPY } from '../../../constants';
import { validateFile } from '../../../utils/dropzoneImageValidator';
import { useDropzone } from 'react-dropzone';
import DetailDisplayRow from '../../../components/DetailDisplayRows/DetailDisplayRow';
import AgencyLogo from '../../../components/AgencyLogo';
import ImageSelector from '../../../components/ImageSelector';
import { updateAgencyLogo } from '../utils/data';
import { useAgency } from '../../../hooks/useAgency';
import placeHolder from '../../../assets/navBarPlaceholder.svg';
import ProtectedElement from '../../../auth/ProtectedElement';
import { agencyInfoEditRoles } from '../../../utils/authorization';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatDateV2 } from '../../../utils/formatDate';
import OfficialCapacityQuestionDialog from './OfficialCapacityQuestionDialog';
import SecondaryButton from '@components/buttons/SecondaryButton';
import AgencyQuestionDialogForm from './AgencyQuestionDialogForm';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';

// matches leftWidth of OfficialDetails, revisit this when we have designs for tablet and mobile view.
const leftWidth = 260;

const agencyKindLabels: Record<string, string> = {
  law_enforcement: 'Law Enforcement',
  other: 'Other'
};

interface AgencyInfoViewProps {
  agencyInfo: Agency;
  setData: (Agency) => void;
  setError: (string) => void;
  setEditDialogOpen: (boolean) => void;
  agencyId?: string | number | undefined;
  agencyQuestions: AgencyQuestion[];
  onAgencyQuestionCreated: (agencyQuestion: AgencyQuestion) => void;
  setAgencyQuestionIdPendingDelete: React.Dispatch<React.SetStateAction<string | null>>;
}

const AgencyInfoView = ({
  agencyInfo,
  setData,
  setError,
  setEditDialogOpen,
  agencyId,
  agencyQuestions,
  onAgencyQuestionCreated,
  setAgencyQuestionIdPendingDelete
}: AgencyInfoViewProps) => {
  const { user } = useUser();
  const { setAgency } = useAgency();
  const theme: Theme = useTheme();

  const [imgUploading, setImgUploading] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState<boolean>(false);
  const [agencyQuestionDialogOpen, setAgencyQuestionDialogOpen] = useState<boolean>(false);

  const onDropAccepted = useCallback(
    async (file) => {
      if (user && agencyId) {
        setImgUploading(true);
        const { data, error } = await updateAgencyLogo(user, file[0], agencyId);
        if (data) {
          setAgency((prevState) => {
            if (!prevState) return null;
            return { ...prevState, logo_url: data.logo_url };
          });
          setData({ ...data });
        } else if (error) {
          console.error(error);
          setError(error);
        }
        setImgUploading(false);
      }
    },
    [setAgency, setData, setError, user, agencyId]
  );

  const validateAgencyLogo = validateFile(5, ['image/jpeg', 'image/jpg', 'image/png']);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDropAccepted,
    maxFiles: 1,
    validator: validateAgencyLogo
  });

  useEffect(() => {
    if (fileRejections.length > 0) {
      let error = fileRejections[0].errors[0].message;
      if (error.includes('Unsuported file') || error.includes('File too large')) {
        error += `. ${COPY.imageUpload.fileType}`;
      }
      setError(error);
    }
  }, [fileRejections, setError]);

  const imageTooltipContent = (
    <ul>
      <li>{COPY.logoUpload.fileType}</li>
      <li>{COPY.logoUpload.size}</li>
      <li>{COPY.logoUpload.background}</li>
    </ul>
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          mb: 4,
          mt: 4
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', md: leftWidth },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          {imgUploading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: '100%'
              }}>
              <CircularProgress size={100} />
            </Box>
          ) : (
            <Box
              {...getRootProps()}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <AgencyLogo
                agencyLogo={agencyInfo.logo_url || placeHolder}
                width={'100%'}
                height={'auto'}
              />
              <ProtectedElement userProfile={user!.profile} allowedRoles={agencyInfoEditRoles}>
                <ImageSelector
                  dropzoneInputProps={getInputProps()}
                  buttonText="Edit Logo"
                  buttonSx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'inherit',
                    color: theme.palette.secondary.main,
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                  tooltipContent={imageTooltipContent}
                  infoIcon={<InfoOutlinedIcon sx={{ color: theme.palette.action.active }} />}
                  infoIconButtonSx={{ padding: 0 }}
                  disableInfoRipple
                />
              </ProtectedElement>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0
          }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
          >
            <Typography component="h2" variant="h5">
              {agencyInfo.name}
            </Typography>
            <ProtectedElement
              userProfile={user!.profile}
              allowedRoles={agencyInfoEditRoles}
            >
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                disableElevation
                disableFocusRipple
                startIcon={<EditIcon fontSize="small" />}
                sx={{
                  borderRadius: 2,
                  borderColor: 'inherit',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  textTransform: 'capitalize'
                }}
                onClick={() => setEditDialogOpen(true)}
              >
                Edit
              </Button>
            </ProtectedElement>
          </Box>
          <DetailDisplayRow
            label="Email Domain"
            capitalize={false}
            value={agencyInfo.email_domain}
          />
          {agencyInfo.contact_email && (
            <DetailDisplayRow
              label="Contact Email"
              capitalize={false}
              value={agencyInfo.contact_email}
            />
          )}
          {agencyInfo.contact_phone && (
            <DetailDisplayRow
              label="Contact Phone"
              capitalize={false}
              value={agencyInfo.contact_phone}
            />
          )}
          {agencyInfo.kind && (
            <DetailDisplayRow
              label="Organization Type"
              capitalize={false}
              value={agencyKindLabels[agencyInfo.kind] || agencyInfo.kind}
            />
          )}
          {agencyInfo.state && (
            <DetailDisplayRow label="State" capitalize={false} value={agencyInfo.state} />
          )}
        </Box>
      </Box>

      <Divider />

      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            marginTop: '32px',
            marginBottom: '24px'
          }}
        >
          <Box>
            <Typography component="h2" variant="h5">
              Additional Verification Questions
            </Typography>
            <Typography sx={{ fontSize: '14px', color: theme.palette.text.secondary, mt: 1 }}>
              Each checkpoint can be configured with additional verification questions, which are
              asked after identity confirmation. Some of these questions will impact the clearance
              outcome of users scanning at checkpoints.
            </Typography>
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            <SecondaryButton
              action={() => setAgencyQuestionDialogOpen(true)}
              startIcon={<AddIcon fontSize="small" />}
              sx={{ fontWeight: 'bold', fontSize: '14px', textTransform: 'capitalize' }}
            >
              Question
            </SecondaryButton>
          </Box>
        </Box>

        {agencyQuestions.map((aq) => (
          <Box
            key={aq.id}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              paddingTop: '16px',
              paddingBottom: '16px'
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 400, fontSize: '16px' }}>{aq.question}</Typography>
                {aq.kind === AgencyQuestionKind.OFFICIAL_CAPACITY && (
                  <IconButton
                    aria-label="more info"
                    data-testid="official-capacity-info-button"
                    disableRipple
                    sx={{ padding: 0, ml: 1 }}
                    onClick={() => {
                      setInfoDialogOpen(true);
                    }}
                  >
                    <InfoOutlinedIcon sx={{ color: theme.palette.action.active }} />
                  </IconButton>
                )}
              </Box>
              {aq.description !== null && (
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '14px',
                    color: theme.palette.text.secondary,
                    mt: '4px'
                  }}
                >
                  {aq.description}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: theme.palette.text.secondary,
                  whiteSpace: 'nowrap'
                }}
              >
                Added on {formatDateV2(aq.created_at)}
              </Typography>
              {aq.kind === AgencyQuestionKind.OFFICIAL_CAPACITY ? (
                <IconButton
                  aria-label={`Agency Question ${aq.question} cannot be deleted`}
                  disabled
                  sx={{
                    width: '36px',
                    height: '30px',
                    borderRadius: '10px'
                  }}
                >
                  <LockIcon sx={{ fontSize: 18 }} />
                </IconButton>
              ) : (
                <IconButton
                  aria-label={`Delete Agency Question ${aq.question}`}
                  onClick={() => {
                    setAgencyQuestionIdPendingDelete(aq.id);
                  }}
                  sx={{
                    color: theme.palette.text.primary,
                    width: '36px',
                    height: '30px',
                    borderRadius: '10px'
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 18, color: theme.palette.text.primary }} />
                </IconButton>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <OfficialCapacityQuestionDialog
        open={infoDialogOpen}
        onClose={() => {
          setInfoDialogOpen(false);
        }}
      />

      <AgencyQuestionDialogForm
        open={agencyQuestionDialogOpen}
        close={() => setAgencyQuestionDialogOpen(false)}
        agencyId={agencyInfo.id}
        onError={setError}
        onSuccess={onAgencyQuestionCreated}
      />
    </>
  );
};

export default AgencyInfoView;
